// DatenView.tsx — #daten: funds & data ONLY (see EinstellungenView's header comment for
// the split rationale). Two sections: Fondsverwaltung (FundManager — fund list with
// enable/disable + coverage + delete/add) and Datenaktualisierung (this file — refresh
// button + per-source health, /api/health rendered per fund + source). This is where the
// "feature disabled, reason: …" honesty (PLAN.md) becomes concrete: a fund with
// meldungCount 0 shows exactly why AGE backtests will be off.
//
// Logout/build-stamp live in #einstellungen (Konto & App) — this view has no account/app
// settings of its own, only funds + data.
import { useEffect, useState } from 'preact/hooks';
import { ApiError, getFunds, getHealth, postRefresh } from '../lib/api.js';
import { formatDateTimeDe } from '../lib/format.js';
import type { FundInfo, HealthFundEntry, HealthResponse, RefreshResponse } from '../lib/types.js';
import { InstallHint } from '../components/InstallHint.js';
import { FundManager } from '../components/FundManager.js';
import { Hint } from '../components/Hint.js';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'success'; data: HealthResponse };

/** GET /api/funds (for the `seeded` flag + delete UI) is loaded independently of
 * /api/health — a failure here must not block the health view, and vice versa; the
 * "Fonds verwalten" card just shows its own empty/error state until funds load. */
type FundsLoadState =
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'success'; funds: FundInfo[] };

export function DatenView() {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [fundsState, setFundsState] = useState<FundsLoadState>({ status: 'loading' });
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState<RefreshResponse | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  function load() {
    setState({ status: 'loading' });
    getHealth()
      .then((data) => setState({ status: 'success', data }))
      .catch((err: unknown) => {
        setState({
          status: 'error',
          error: err instanceof ApiError ? err.message : 'Unbekannter Fehler beim Laden.',
        });
      });
  }

  function loadFunds() {
    setFundsState({ status: 'loading' });
    getFunds()
      .then((data) => setFundsState({ status: 'success', funds: data.funds }))
      .catch((err: unknown) => {
        setFundsState({
          status: 'error',
          error: err instanceof ApiError ? err.message : 'Unbekannter Fehler beim Laden.',
        });
      });
  }

  useEffect(load, []);
  useEffect(loadFunds, []);

  /** After adding/removing a fund: both the fund list (delete buttons, seeded flags) and
   * the health/coverage cards below need to reflect it. */
  function handleFundsChanged() {
    loadFunds();
    load();
  }

  async function handleRefresh() {
    setRefreshing(true);
    setRefreshError(null);
    setRefreshResult(null);
    try {
      const res = await postRefresh();
      setRefreshResult(res);
      load();
    } catch (err) {
      setRefreshError(err instanceof ApiError ? err.message : 'Unbekannter Fehler beim Aktualisieren.');
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div>
      {fundsState.status === 'loading' ? (
        <div class="card">
          <h2>Fondsverwaltung</h2>
          <div class="empty-state">
            <span class="spinner" /> Wird geladen…
          </div>
        </div>
      ) : fundsState.status === 'error' ? (
        <div class="card">
          <h2>Fondsverwaltung</h2>
          <div class="banner banner-error" role="alert">
            <strong>Fonds konnten nicht geladen werden</strong>
            <span>{fundsState.error}</span>
          </div>
        </div>
      ) : (
        <FundManager funds={fundsState.funds} onChanged={handleFundsChanged} />
      )}

      <div class="card">
        <h2>Datenaktualisierung</h2>
        <Hint>
          <p class="field-hint">
            Kursdaten, Ausschüttungen und OeKB-Meldungen von den externen Quellen abrufen; Status je Fonds und Quelle.
          </p>
        </Hint>
        {state.status === 'success' ? (
          <p class="field-hint">Stand: {formatDateTimeDe(state.data.generatedAt)}</p>
        ) : null}
        <button type="button" class="btn btn-primary btn-block" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? <span class="spinner" /> : 'Jetzt aktualisieren'}
        </button>
        {refreshError ? (
          <div class="banner banner-error" role="alert" style={{ marginTop: '0.6rem' }}>
            <strong>Aktualisierung fehlgeschlagen</strong>
            <span>{refreshError}</span>
          </div>
        ) : null}
        {refreshResult ? <RefreshResultList result={refreshResult} /> : null}
      </div>

      {state.status === 'loading' ? (
        <div class="empty-state">
          <span class="spinner" /> Wird geladen…
        </div>
      ) : null}

      {state.status === 'error' ? (
        <div class="banner banner-error" role="alert">
          <strong>Offline oder Server nicht erreichbar</strong>
          <span>{state.error}</span>
        </div>
      ) : null}

      {state.status === 'success' && state.data.funds.length === 0 ? (
        <div class="empty-state">
          Noch keine Fonds hinterlegt — im Backend anlegen und hier „Jetzt aktualisieren"
          drücken.
        </div>
      ) : null}

      {state.status === 'success' ? state.data.funds.map((f) => <FundHealthCard key={f.isin} fund={f} />) : null}

      <InstallHint />
    </div>
  );
}

function RefreshResultList({ result }: { result: RefreshResponse }) {
  if (!Array.isArray(result.results) || result.results.length === 0) {
    return <p class="field-hint">Aktualisierung abgeschlossen.</p>;
  }
  return (
    <ul class="source-list" style={{ marginTop: '0.6rem' }}>
      {result.results.map((r, i) => (
        <li key={i} class="source-row">
          <span>
            {r.isin ?? '—'} {r.source ? `· ${r.source}` : ''}
          </span>
          <span>
            <StatusDot ok={r.ok ?? null} />
            {r.ok === false ? (r.error ?? 'Fehler') : 'OK'}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** null = never fetched successfully or with an error; true/false = which happened most recently. */
function sourceStatus(s: { lastSuccessAt: string | null; lastErrorAt: string | null }): boolean | null {
  if (!s.lastSuccessAt && !s.lastErrorAt) return null;
  if (!s.lastErrorAt) return true;
  if (!s.lastSuccessAt) return false;
  return s.lastSuccessAt >= s.lastErrorAt;
}

function FundHealthCard({ fund }: { fund: HealthFundEntry }) {
  return (
    <div class="card">
      <h3>
        {fund.name} <span class="faint">({fund.isin})</span>
      </h3>

      <div class="section-title">Quellen</div>
      <ul class="source-list">
        {fund.sources.map((s) => (
          <li key={s.source} class="source-row">
            <span>
              <StatusDot ok={sourceStatus(s)} />
              {s.source}
            </span>
            <span class="faint">
              {s.lastSuccessAt ? `OK: ${formatDateTimeDe(s.lastSuccessAt)}` : 'noch nie erfolgreich'}
              {s.lastErrorAt ? ` · Fehler: ${formatDateTimeDe(s.lastErrorAt)}` : ''}
            </span>
          </li>
        ))}
      </ul>
      {fund.sources.some((s) => s.lastErrorMessage) ? (
        <div class="banner banner-neutral" style={{ marginTop: '0.5rem' }}>
          <strong>Letzte Fehlermeldungen</strong>
          {fund.sources
            .filter((s) => s.lastErrorMessage)
            .map((s) => (
              <div key={s.source} class="faint">
                {s.source}: {s.lastErrorMessage}
              </div>
            ))}
        </div>
      ) : null}

      <div class="section-title">Abdeckung</div>
      <CoverageList coverage={fund.coverage} />
    </div>
  );
}

/** Rendered generically (field names by key) since the health payload's coverage shape
 * is the server worker's to define — see lib/types.ts. */
function CoverageList({ coverage }: { coverage: Record<string, unknown> }) {
  const entries = Object.entries(coverage);
  if (entries.length === 0) {
    return <p class="field-hint">Keine Abdeckungsdaten.</p>;
  }
  return (
    <dl class="kv-list">
      {entries.map(([key, value]) => (
        <>
          <dt key={`${key}-k`}>{key}</dt>
          <dd key={`${key}-v`} style={{ margin: 0 }}>
            {formatCoverageValue(value)}
          </dd>
        </>
      ))}
    </dl>
  );
}

function formatCoverageValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function StatusDot({ ok }: { ok: boolean | null }) {
  const cls = ok === null ? 'dot-unknown' : ok ? 'dot-ok' : 'dot-error';
  return <span class={`dot ${cls}`} aria-hidden="true" />;
}
